// ABOUTME: Main SaaS billing component for subscription management and pricing
// ABOUTME: Provides comprehensive billing interface for HR teams using Sourcerer AI

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import SubscriptionPlans from './SubscriptionPlans';
import BillingManagement from './BillingManagement';
import { CreditCard, Package, Settings, TrendingUp } from 'lucide-react';

const Billing = () => {
  const {
    subscriptionStatus
  } = useSubscriptions();
  const [activeTab, setActiveTab] = useState('plans');

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Billing & Subscriptions</h1>
          <p className="text-gray-600 mt-1">
            Manage your Sourcerer AI subscription and billing preferences
          </p>
        </div>
        <div className="flex gap-2">
          {subscriptionStatus?.hasSubscription && <Button variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>}
        </div>
      </div>

      {/* Subscription Status Banner */}
      {subscriptionStatus?.hasSubscription ? (
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-900">Active Subscription</h3>
                  <p className="text-green-700">Your Sourcerer AI subscription is active and ready to use</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-green-600">
                  {subscriptionStatus.subscriptions.length} active subscription{subscriptionStatus.subscriptions.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Choose Your Plan</h3>
                  <p className="text-blue-700">Select a subscription plan to unlock all Sourcerer AI features</p>
                </div>
              </div>
              <Button onClick={() => setActiveTab('plans')} className="text-slate-50">View Plans Below</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="grid w-full grid-cols-3 items-center justify-center">
          <TabsList className="col-start-2 justify-self-center">
            <TabsTrigger value="plans" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Plans
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6">
          <BillingManagement />
        </TabsContent>

        <TabsContent value="plans" className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Choose the Perfect Plan</h2>
            <p className="text-gray-600">
              Select a plan that fits your HR team's needs and scale as you grow
            </p>
          </div>
          <SubscriptionPlans />
        </TabsContent>

        <TabsContent value="management" className="space-y-6">
          <BillingManagement />
        </TabsContent>
      </Tabs>

      {/* Features Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Why Choose Sourcerer AI?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Advanced Analytics</h3>
              <p className="text-sm text-gray-600">
                Get deep insights into your referral program performance with comprehensive analytics
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Scalable Solution</h3>
              <p className="text-sm text-gray-600">
                From small teams to enterprise organizations, our platform scales with your needs
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Settings className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">Easy Management</h3>
              <p className="text-sm text-gray-600">
                Intuitive interface makes it easy to manage referrals, track progress, and process payments
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Billing;
